import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * InfoTooltip - A tooltip component that works with hover + click on desktop, tap on mobile
 * 
 * Features:
 * - Desktop: Opens on hover, can be locked open with click
 * - Mobile: Opens/closes on tap
 * - Closes on click outside, Escape key, or blur
 */
const InfoTooltip = ({ 
  content, 
  className = '',
  iconSize = 14,
  side = 'top',
  align = 'center'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        contentRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsLocked(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setIsLocked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLocked) {
      setIsOpen(false);
      setIsLocked(false);
    } else {
      setIsOpen(true);
      setIsLocked(true);
    }
  };

  const handleMouseEnter = () => {
    if (!isLocked) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isLocked) {
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = () => {
    if (!isLocked) {
      setIsOpen(false);
    }
  };

  // Position classes based on side and align
  const getPositionClasses = () => {
    const positions = {
      top: 'bottom-full mb-2',
      bottom: 'top-full mt-2',
      left: 'right-full mr-2',
      right: 'left-full ml-2',
    };

    const alignments = {
      start: side === 'top' || side === 'bottom' ? 'left-0' : 'top-0',
      center: side === 'top' || side === 'bottom' ? 'left-1/2 -translate-x-1/2' : 'top-1/2 -translate-y-1/2',
      end: side === 'top' || side === 'bottom' ? 'right-0' : 'bottom-0',
    };

    return `${positions[side]} ${alignments[align]}`;
  };

  return (
    <span className={cn('relative inline-flex items-center', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'inline-flex items-center justify-center',
          'text-foreground-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1 focus:ring-offset-transparent rounded-full',
          'transition-colors cursor-pointer'
        )}
        aria-label="More information"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Info 
          style={{ width: iconSize, height: iconSize }}
        />
      </button>

      {isOpen && (
        <div
          ref={contentRef}
          role="tooltip"
          className={cn(
            'absolute z-50 w-64 sm:w-80 p-3 rounded-lg',
            'bg-slate-800 border border-white/10 shadow-xl',
            'text-xs text-foreground leading-relaxed',
            'animate-in fade-in-0 zoom-in-95 duration-200',
            getPositionClasses()
          )}
        >
          {content}
        </div>
      )}
    </span>
  );
};

export default InfoTooltip;
